import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, alpha, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, MenuItem,
  Snackbar, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { apiGetAll, apiPost } from 'api/service';
import { createScanTemplate, deleteScanTemplate, updateScanTemplate, markScanTemplateUsed } from 'api/features';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import ScopePicker from './ScopePicker';
import { GREEN } from './scanUi';
import {
  CARE, FAMILIES, FAMILY_COLOR, KINDS, STARTERS, WEB_DEPTHS, buildRuns, canRun, filterTemplates, tags, toPayload
} from './templateLogic';

const BLANK = { name: '', family: 'web', note: '', depth: 'balanced', kind: 'network_port_scan', care: 'balanced', shared: false };
const card = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper', p: 2.5 };
const FamilyChip = ({ family }) => (
  <Chip size="small" label={FAMILIES[family] || String(family || '').toUpperCase()}
    sx={{ fontWeight: 700, fontSize: 11, borderRadius: 1.5, bgcolor: alpha(FAMILY_COLOR[family] || '#64748b', 0.16), color: FAMILY_COLOR[family] || 'text.secondary' }} />
);

export default function ScanTemplatesPage() {
  const { currentTargetId, targets } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)) || null, [targets, currentTargetId]);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState('all');
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null); // {id?, ...fields} while the editor is open
  const [runFor, setRunFor] = useState(null);
  const [toast, setToast] = useState(null);
  const say = (severity, message) => setToast({ severity, message });

  const load = useCallback(async () => {
    try { setTemplates(await apiGetAll('/api/scan-templates/')); } catch (e) { say('error', e?.message || 'Could not load templates.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => filterTemplates(templates, family, q), [templates, family, q]);
  const count = (f) => (f === 'all' ? templates.length : templates.filter((t) => t.scan_type === f).length);

  const save = async () => {
    if (!form.name.trim()) return say('error', 'Give the template a name.');
    try {
      const payload = toPayload(form);
      if (form.id) await updateScanTemplate(form.id, payload); else await createScanTemplate(payload);
      setForm(null); say('success', 'Template saved.'); load();
    } catch (e) { say('error', e?.message || 'Could not save.'); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await deleteScanTemplate(t.id); say('success', 'Template deleted.'); load(); } catch (e) { say('error', e?.message || 'Could not delete.'); }
  };
  const edit = (t) => { const m = t.metadata || {}; setForm({ ...BLANK, id: t.id, name: t.name, family: t.scan_type, note: m.note || '', depth: m.depth || 'balanced', kind: m.kind || BLANK.kind, care: m.care || 'balanced', shared: t.shared }); };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Scan templates</Typography>
          <Typography color="text.secondary">Save a scan setup once, then run it on any target in two clicks</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({ ...BLANK })} sx={{ height: 44, px: 3, fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>New template</Button>
      </Stack>

      <Typography fontWeight={600} sx={{ mb: 1.5 }}>Start from a ready-made setup</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, mb: 4 }}>
        {STARTERS.map((s) => (
          <Box key={s.key} sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', fontWeight: 700, bgcolor: alpha(FAMILY_COLOR[s.family], 0.16), color: FAMILY_COLOR[s.family] }}>{s.letter}</Box>
            <Typography fontWeight={700}>{s.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{s.note}</Typography>
            <Button size="small" onClick={() => setForm({ ...BLANK, ...s })} sx={{ alignSelf: 'flex-start', color: GREEN, fontWeight: 700, px: 0 }}>Use this setup →</Button>
          </Box>
        ))}
      </Box>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={family} onChange={(_, v) => v && setFamily(v)}>
          {['all', 'web', 'network'].map((f) => <ToggleButton key={f} value={f} sx={{ textTransform: 'none', px: 2, borderRadius: '999px !important', fontWeight: 600 }}>{f === 'all' ? 'All' : FAMILIES[f]} {count(f)}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField size="small" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates" sx={{ ml: 'auto', minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Stack>

      {!loading && !shown.length && <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{templates.length ? 'No template matches.' : 'No templates yet. Pick a ready-made setup above or create your own.'}</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 2, alignItems: 'start' }}>
        {shown.map((t) => (
          <Box key={t.id} sx={{ ...card, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>{t.name}</Typography>
                <Typography variant="body2" color="text.secondary">{t.metadata?.note || (canRun(t) ? 'No description' : 'Older template without saved settings')}</Typography>
              </Box>
              <FamilyChip family={t.scan_type} />
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.8}>{tags(t).map((g) => <Chip key={g} size="small" label={g} />)}</Stack>
            <Stack direction="row" alignItems="center" gap={1} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>Used {t.uses_count} time{t.uses_count === 1 ? '' : 's'}{t.shared ? ' · shared' : ''}</Typography>
              {t.is_owner && <Button size="small" color="inherit" onClick={() => edit(t)}>Edit</Button>}
              {t.is_owner && <Button size="small" color="error" onClick={() => remove(t)}>Delete</Button>}
              <Button size="small" variant="contained" disabled={!canRun(t)} onClick={() => setRunFor(t)} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Run on…</Button>
            </Stack>
          </Box>
        ))}
      </Box>

      <Editor form={form} setForm={setForm} onSave={save} />
      <RunDialog template={runFor} userTarget={userTarget} onClose={() => setRunFor(null)} onDone={(n) => { setRunFor(null); say('success', `${n} scan${n === 1 ? '' : 's'} launched.`); load(); }} onError={(m) => say('error', m)} />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Box>
  );
}

function Editor({ form, setForm, onSave }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const choose = (list, key, valueKey = 'id') => list.map((o) => <MenuItem key={o[valueKey]} value={o[valueKey]}>{o.title || o.label} — {o.desc}</MenuItem>);
  return (
    <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
      {form && <>
        <DialogTitle>{form.id ? 'Edit template' : 'New template'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={set('name')} autoFocus fullWidth />
            <TextField label="What it is for (optional)" value={form.note} onChange={set('note')} fullWidth />
            <TextField select label="Type" value={form.family} onChange={set('family')} disabled={!!form.id}>
              <MenuItem value="web">Web</MenuItem><MenuItem value="network">Network</MenuItem>
            </TextField>
            {form.family === 'web'
              ? <TextField select label="How deep" value={form.depth} onChange={set('depth')}>{choose(WEB_DEPTHS)}</TextField>
              : <>
                <TextField select label="What to find out" value={form.kind} onChange={set('kind')}>{choose(KINDS, 'kind', 'value')}</TextField>
                <TextField select label="How careful" value={form.care} onChange={set('care')}>{choose(CARE)}</TextField>
              </>}
            <FormControlLabel control={<Switch checked={form.shared} onChange={(e) => setForm((f) => ({ ...f, shared: e.target.checked }))} />} label="Share with everyone on the platform" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button color="inherit" onClick={() => setForm(null)}>Cancel</Button>
          <Button variant="contained" onClick={onSave} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Save template</Button>
        </DialogActions>
      </>}
    </Dialog>
  );
}

function RunDialog({ template, userTarget, onClose, onDone, onError }) {
  const [assets, setAssets] = useState([]);
  const [picked, setPicked] = useState([]);
  const [starting, setStarting] = useState(false);
  const target = userTarget?.target;

  useEffect(() => {
    setPicked([]);
    if (template && userTarget) apiGetAll(`/api/assets/?user_target=${userTarget.id}`).then(setAssets).catch(() => setAssets([]));
  }, [template, userTarget]);

  const rows = useMemo(() => {
    if (!target) return [];
    const root = { id: 'root', value: target.value, label: userTarget.name || target.value, kind: 'Main address' };
    const seen = new Set([String(root.value).toLowerCase()]);
    const rest = assets.filter((a) => { const k = a.value.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
      .map((a) => ({ id: a.id, value: a.value, label: a.label || a.value, type: a.type, kind: a.type === 'url' ? 'Website' : a.type === 'ip' ? 'IP address' : 'Subdomain' }));
    return [root, ...rest];
  }, [target, userTarget, assets]);
  const isPicked = (id) => (picked.length ? picked.includes(id) : id === 'root');
  const chosen = rows.filter((r) => isPicked(r.id));

  const start = async () => {
    if (chosen.length > 25 && !window.confirm(`This starts ${chosen.length} scans, one per address. Continue?`)) return;
    setStarting(true);
    try {
      const requests = buildRuns(template, userTarget.id, chosen);
      await Promise.all(requests.map((r) => apiPost('/api/scans/', r)));
      markScanTemplateUsed(template.id).catch(() => {});
      onDone(requests.length);
    } catch (e) { onError(e?.message || 'Could not start the scans.'); }
    setStarting(false);
  };

  return (
    <Dialog open={!!template} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Run "{template?.name}" on…</DialogTitle>
      <DialogContent>
        {!userTarget ? <Alert severity="info">Choose a target at the top of the page first.</Alert> : <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Pick the addresses of {userTarget.name || target.value}. One scan starts per address.</Typography>
          <ScopePicker rows={rows} isPicked={isPicked} onToggle={(id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} onSetMany={setPicked} selectedCount={chosen.length} />
        </>}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={starting || !chosen.length} onClick={start} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>
          {starting ? 'Starting…' : `Start ${chosen.length} scan${chosen.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
